import React, { useCallback } from 'react';
import { TextFieldProps } from '@mui/material';
import { useField, ErrorMessage, useFormikContext } from 'formik';

import { makeCallAll } from '~/common';
import { AccountExistsError } from '~/src/components/AccountExistsError';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { useSSRSafeFormInputAttributes } from '~/src/components/hooks/useSSRSafeFormInputAttributes';

import { SetFormEmailFromUrl } from './SetFormEmailFromUrl';
import { useEmailValidation } from './useEmailValidation';
import { useInitialValidation } from './useInitialValidation';
import { useUpdateFormikStatus } from './useUpdateFormikStatus';
import { FormInput } from '../elements';

type FormikInputParams = {
  label: string;
  fieldName: string;
  initialValidation?: boolean;
  prefillFromUrl?: boolean;
} & TextFieldProps;

export type EmailValidationStatus = 'idle' | 'invalid' | 'valid';

export function EmailInput(props: FormikInputParams): React.ReactElement {
  const { fieldName, initialValidation = true, prefillFromUrl = false, ...restProps } = props;

  const { status } = useFormikContext();
  const [field, meta] = useField(fieldName);

  const { emailValidationState, validateEmail, onEmailChanged } = useEmailValidation();

  useInitialValidation({
    fieldName,
    isInitialValidationRequired: initialValidation,
    validateEmail,
  });

  useUpdateFormikStatus({ emailValidationState });

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const email = (event.target as HTMLInputElement).value;
      validateEmail(email);
    },
    [validateEmail],
  );

  const onEnterKeyUP = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const email = (event.target as HTMLInputElement).value;
        validateEmail(email);
      }
    },
    [validateEmail],
  );

  const isSSR = useIsSSR();

  const emailSSRSafeAtttibutes = useSSRSafeFormInputAttributes('email');
  return (
    <>
      {prefillFromUrl && <SetFormEmailFromUrl fieldName="email" onChanged={validateEmail} />}
      <FormInput
        {...field}
        {...restProps}
        id={fieldName}
        onBlur={makeCallAll(field.onBlur, onBlur)}
        onChange={makeCallAll(field.onChange, onEmailChanged)}
        onKeyUp={onEnterKeyUP}
        error={meta.touched && Boolean(meta.error)}
        disabled={isSSR}
        helperText={
          meta.error ? (
            <ErrorMessage name={fieldName} render={msg => <span role="alert">{msg}</span>} />
          ) : status?.emailValidationStatus === 'invalid' && status?.serverErrors?.email ? (
            <AccountExistsError email={field.value} errorMessage={status.serverErrors.email} />
          ) : (
            ''
          )
        }
        {...emailSSRSafeAtttibutes}
      />
    </>
  );
}
